import { useState } from 'react';
import { Input } from '@react-apps/design-system';
import type { ChangePasswordInput } from './reauthenticate-and-change-password';

/**
 * Mínimo de caracteres — **espelha o backend**, que valida `@MinLength(8)` no
 * `ChangePasswordRequestDto`. A política de 8 é decisão registrada
 * (`force-password-change-plan.md`: a senha temporária subiu de 6 → 8 de
 * propósito, porque afrouxar justamente na senha que um terceiro conhece é o
 * inverso do que a situação pede).
 *
 * Se o backend endurecer, mude **aqui**. Enquanto divergir, o 400 da validação
 * cai no `VALIDATION_REJECTED` de `messageForError` — legível, em vez do texto
 * cru do axios.
 */
export const PASSWORD_MIN_LENGTH = 8;

export interface ForcedPasswordChangeFormProps {
  /**
   * A temporária em mão. É a credencial da reautenticação — sem ela não há o
   * que trocar — e serve à barreira de "não repita a temporária", abaixo.
   */
  tempPassword: string;
  isLoading: boolean;
  /** Erro vindo do hook (Firebase/backend). O do formulário é local. */
  error: string | null;
  onSubmit: (input: ChangePasswordInput) => void;
  /** Só para testar a divergência; o padrão é o que o backend exige. */
  minLength?: number;
}

/**
 * O formulário da troca obrigatória: os dois campos, as validações locais e o
 * botão.
 *
 * Só o formulário, não a tela: o cromo em volta (logo, observer de tema,
 * rodapé) é o mesmo do sign-in e do esqueci-a-senha, então é assunto de um
 * layout no design-system — não deste pacote, que não deveria ser dono da marca.
 */
export function ForcedPasswordChangeForm({
  tempPassword,
  isLoading,
  error,
  onSubmit,
  minLength = PASSWORD_MIN_LENGTH,
}: ForcedPasswordChangeFormProps) {
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [formError, setFormError] = useState<string | null>(null);

  const handleSubmit = (e: { preventDefault(): void }) => {
    e.preventDefault();
    if (password.length < minLength) {
      setFormError(
        `A senha precisa ter no mínimo ${String(minLength)} caracteres.`,
      );
      return;
    }
    if (password !== confirmPassword) {
      setFormError('As senhas não coincidem.');
      return;
    }
    // Barreira de UX (não é controle de segurança): a nova não pode ser a
    // temporária que um terceiro conhece.
    if (password === tempPassword) {
      setFormError('A nova senha precisa ser diferente da temporária.');
      return;
    }
    setFormError(null);
    onSubmit({ currentPassword: tempPassword, newPassword: password });
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-5">
      <Input
        id="password"
        label="Nova senha"
        type="password"
        required
        autoComplete="new-password"
        value={password}
        onChange={(e) => setPassword(e.target.value)}
        placeholder="••••••••"
      />

      <Input
        id="confirmPassword"
        label="Confirmar senha"
        type="password"
        required
        autoComplete="new-password"
        value={confirmPassword}
        onChange={(e) => setConfirmPassword(e.target.value)}
        placeholder="••••••••"
      />

      {(formError ?? error) && (
        <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 dark:border-red-900 dark:bg-red-950 dark:text-red-400">
          {formError ?? error}
        </div>
      )}

      <button
        type="submit"
        disabled={isLoading || !password || !confirmPassword}
        className="bg-(--color-accent) w-full rounded-lg px-4 py-2.5 text-sm font-semibold text-white transition hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50"
      >
        {isLoading ? 'Salvando…' : 'Salvar e entrar'}
      </button>
    </form>
  );
}
